/**
 * Notice when the app running in this webview is not the one on the server.
 *
 * A Mini App is a page inside Telegram's webview, and that webview holds on to
 * index.html harder than a browser does. The built bundle is content-hashed, so
 * a new deploy is a new filename — but a stale index.html points at the old
 * filename, and the app keeps running yesterday's code against today's server
 * with nothing on screen to say so. A feature that shipped an hour ago is
 * simply missing, and a control it was supposed to light up sits there dead.
 *
 * So the app asks, once, on boot: which bundle does the server's index.html
 * name? If it is not the one executing, this page is stale and reloads itself.
 *
 * Three things keep that from becoming a loop: it reloads at most once per
 * session, it only acts on a clear mismatch — never on a failed check — and it
 * cache-busts the reload so the webview cannot hand back the same HTML again.
 */

const RELOADED = 'pxc.reloadedForVersion';

/** The filename of the bundle currently executing, e.g. "index-BxxfQ5Gb.js". */
function runningBundle(): string | null {
  try {
    // Vite rewrites this to the chunk's own URL at build time.
    const url = new URL(import.meta.url);
    const name = url.pathname.split('/').pop();
    return name && name.endsWith('.js') ? name : null;
  } catch {
    return null;
  }
}

/**
 * Reloads the page if the server is serving a newer build than this one.
 * Resolves either way; the caller does not need to wait for it.
 *
 * Nothing happens unless the code executing is a built, hashed chunk: under the
 * dev server the modules are the sources themselves, whose names will never
 * match what the built index.html points at, and a reload there would be a
 * loop. `running` is the seam for that — a test cannot set the module's own
 * URL, so it passes the name in.
 */
export async function reloadIfStale(running = runningBundle()): Promise<void> {
  if (!running) return;

  let served: string | null = null;
  try {
    const response = await fetch('/api/v1/version', { cache: 'no-store' });
    if (!response.ok) return;
    const body = (await response.json()) as { bundle?: unknown };
    served = typeof body.bundle === 'string' ? body.bundle : null;
  } catch {
    return;
  }
  if (!served || served === running) return;

  try {
    if (sessionStorage.getItem(RELOADED) === served) return;
    sessionStorage.setItem(RELOADED, served);
  } catch {
    // No session storage means no way to be sure this is the first try, and a
    // reload loop is worse than an old bundle.
    return;
  }

  const url = new URL(window.location.href);
  url.searchParams.set('v', served);
  window.location.replace(url.toString());
}
